import {
  IDLE_CONFIG,
  MIN_IMPLIED_SPEED_DELTA_MS,
  determineVehicleStatus,
  impliedSpeedFromDistance,
  impliedSpeedFromOdometer,
  isStaleTimestamp,
  odometerKmFromParams,
  resolveEffectiveSpeed,
  timestampToMs,
} from "../store/shared/vehicleStatusRule";
import {
  FLAG_TYPES,
  TRANSFORM_RANGES,
  describeMetricSources,
  findParamSource,
  resolveMetricsFromParams,
  type MetricKey,
  type MetricSourceCandidate,
  type ParamSourceHit,
  type RawParams,
} from "./vehicleParamsResolver";

export type StatusRuleId =
  | "noTimestamp"
  | "stale"
  | "moving"
  | "idleSignalOn"
  | "idleSignalOff"
  | "ignitionOnIdle"
  | "ignitionOnStopped"
  | "ignitionOff"
  | "speedKnown"
  | "fallback";

export type SpeedSource = "reported" | "odometer" | "distance" | "none";

export type SpeedUnavailableReason =
  | "notReported"
  | "noPreviousValue"
  | "gpsGlitch"
  | "noTimestamps"
  | "deltaTooShort"
  | "noProgress";

export type IgnitionSource = "engineStatus" | "frameIgnition" | "param" | "none";

export interface SpeedCandidate {
  source: SpeedSource;
  value: number | null;
  reason: SpeedUnavailableReason | null;
}

export interface IgnitionExplanation {
  value: boolean | null;
  source: IgnitionSource;
  engineStatus: number | null;
  frameIgnition: boolean | null;
  paramKey: string | null;
  paramRaw: number | string | null;
  paramScope: string | null;
  inCurrentFrame: boolean;
  expected: string[];
}

export interface IdleExplanation {
  value: boolean | null;
  supported: boolean;
  expected: string[];
  paramKey: string | null;
  paramRaw: number | string | null;
  paramScope: string | null;
  inCurrentFrame: boolean;
  inferredFromIgnition: boolean;
  idleForMs: number | null;
  thresholdMs: number;
}

export type MetricOrigin = "frameField" | "param" | "previous" | "none";

export interface MetricExplanation {
  metric: MetricKey;
  value: number | boolean | null;
  origin: MetricOrigin;
  originKey: string | null;
  originRaw: number | string | boolean | null;
  inCurrentFrame: boolean;
  unusedParam: boolean;
  present: boolean;
  rejected: boolean;
  paramKey: string | null;
  paramRaw: number | string | null;
  transform: string | null;
  validRange: string | null;
  scope: string | null;
  expected: string[];
}

export interface StatusFrameInput {
  params?: RawParams;
  speed?: number | null;
  ignition?: boolean | null;
  engineStatus?: number | null;
  eventCode?: number | null;
  battery?: number | null;
  timestamp?: number | null;
  lat?: number | null;
  lon?: number | null;
  brandModel?: string | null;
  brandName?: string | null;
}

export interface StatusChainState {
  params: Record<string, number | string>;
  status?: string;
  idleSince?: number | null;
  odometerKm?: number | null;
  battery?: number | null;
  gpsSat?: number | null;
  engineCutoff?: boolean | null;
  hitch?: boolean | null;
  lat?: number | null;
  lon?: number | null;
  timestamp?: number | null;
  brandModel?: string | null;
  brandName?: string | null;
}

export interface StatusExplanation {
  status: string;
  rule: StatusRuleId;
  idleSince: number | null;
  timestampMs: number | null;
  isStale: boolean;
  previousStatus: string | null;
  reportedSpeed: number | null;
  effectiveSpeed: number | null;
  speedSource: SpeedSource;
  speedCandidates: SpeedCandidate[];
  isMoving: boolean;
  movementThreshold: number;
  deltaMs: number | null;
  minDeltaMs: number;
  ignition: IgnitionExplanation;
  idle: IdleExplanation;
  metrics: MetricExplanation[];
  params: Record<string, number | string>;
  changedKeys: string[];
  brandModel: string;
  brandName: string;
  next: StatusChainState;
}

const PANIC_EVENT_CODES = new Set<number>([5]);

const ENGINE_ON_CODES = new Set<number>([1, 2, 3]);

export const mergeParams = (
  existing: Record<string, number | string> | undefined,
  incoming: RawParams,
): { params: Record<string, number | string>; changedKeys: string[] } => {
  const base = existing ?? {};
  if (!incoming || typeof incoming !== "object") return { params: base, changedKeys: [] };

  const changedKeys = Object.keys(incoming).filter((key) => {
    const value = incoming[key];
    if (value === null || value === undefined) return false;
    if (typeof value !== "number" && typeof value !== "string") return false;
    return base[key] !== value;
  });

  if (changedKeys.length === 0) return { params: base, changedKeys };

  const params = changedKeys.reduce(
    (acc, key) => ({ ...acc, [key]: incoming[key] as number | string }),
    { ...base },
  );

  return { params, changedKeys };
};

const derivePanic = (
  rawPanic: number | null | undefined,
  eventCode: number | null | undefined,
): number | null => {
  if (rawPanic != null) return Number(rawPanic);
  const code = eventCode != null ? Number(eventCode) : null;
  if (code != null && PANIC_EVENT_CODES.has(code)) return code;
  return null;
};

const speedReason = (
  hasPrevious: boolean,
  glitch: boolean,
  hasTimestamps: boolean,
  deltaMs: number | null,
): SpeedUnavailableReason => {
  if (!hasPrevious) return "noPreviousValue";
  if (glitch) return "gpsGlitch";
  if (!hasTimestamps) return "noTimestamps";
  if (deltaMs != null && deltaMs < MIN_IMPLIED_SPEED_DELTA_MS) return "deltaTooShort";
  return "noProgress";
};

interface CandidateHit {
  candidate: MetricSourceCandidate;
  hit: ParamSourceHit | null;
}

const findCandidateHit = (
  params: RawParams,
  candidates: MetricSourceCandidate[],
): CandidateHit | null =>
  candidates
    .map((candidate) => ({ candidate, hit: findParamSource(params, candidate.sources) }))
    .find((entry) => entry.hit != null) ?? null;

const findActiveFlagHit = (
  params: RawParams,
  candidates: MetricSourceCandidate[],
): CandidateHit | null =>
  candidates
    .flatMap((candidate) =>
      candidate.sources.map((source) => ({
        candidate,
        hit: findParamSource(params, source),
      })),
    )
    .find(
      (entry) =>
        entry.hit != null && FLAG_TYPES[entry.candidate.transform]?.(entry.hit.value),
    ) ?? null;

interface MetricInput {
  metric: MetricKey;
  candidates: MetricSourceCandidate[];
  params: RawParams;
  frameParams: RawParams;
  value: number | boolean | null;
  paramValue: number | boolean | null;
  frameField?: { key: string; raw: number | string | boolean } | null;
  previousValue?: number | boolean | null;
  candidateHit?: CandidateHit | null;
}

const explainMetric = ({
  metric,
  candidates,
  params,
  frameParams,
  value,
  paramValue,
  frameField,
  previousValue,
  candidateHit,
}: MetricInput): MetricExplanation => {
  const found =
    candidateHit === undefined ? findCandidateHit(params, candidates) : candidateHit;
  const transform = found?.candidate.transform ?? null;

  const origin: MetricOrigin = frameField
    ? "frameField"
    : paramValue != null
      ? "param"
      : previousValue != null
        ? "previous"
        : "none";

  return {
    metric,
    value,
    origin,
    originKey: origin === "frameField" ? frameField?.key ?? null : found?.hit?.key ?? null,
    originRaw: origin === "frameField" ? frameField?.raw ?? null : found?.hit?.raw ?? null,
    inCurrentFrame: findCandidateHit(frameParams, candidates) != null,
    unusedParam: origin !== "param" && found != null,
    present: found != null,
    rejected: found != null && paramValue == null,
    paramKey: found?.hit?.key ?? null,
    paramRaw: found?.hit?.raw ?? null,
    transform,
    validRange: transform ? TRANSFORM_RANGES[transform] ?? null : null,
    scope: found?.candidate.scope ?? null,
    expected: candidates.flatMap((candidate) => candidate.sources),
  };
};

export const explainStatus = (
  frame: StatusFrameInput,
  previous?: StatusChainState,
): StatusExplanation => {
  const { params, changedKeys } = mergeParams(previous?.params, frame.params);

  const brandModel = frame.brandModel ?? previous?.brandModel ?? "";
  const brandName = frame.brandName ?? previous?.brandName ?? "";
  const resolved = resolveMetricsFromParams(params, brandModel, brandName);
  const sources = describeMetricSources(brandModel, brandName);

  const odometerKm = odometerKmFromParams(params) ?? previous?.odometerKm ?? null;
  const battery = frame.battery ?? resolved.battery ?? previous?.battery ?? null;
  const gpsSat =
    findParamSource(frame.params, "gps_sat")?.value ??
    resolved.satellites ??
    previous?.gpsSat ??
    null;

  const engineStatus = frame.engineStatus ?? null;
  const frameIgnition = frame.ignition ?? null;
  const directIgnition =
    engineStatus != null || frameIgnition != null
      ? ENGINE_ON_CODES.has(Number(engineStatus)) || frameIgnition === true
      : null;
  const ignitionState = directIgnition ?? resolved.ignition;

  const timestamp = frame.timestamp ?? null;
  const previousTimestamp = previous?.timestamp ?? null;
  const deltaMs =
    timestamp && previousTimestamp
      ? timestampToMs(timestamp) - timestampToMs(previousTimestamp)
      : null;

  const impliedOdometer = impliedSpeedFromOdometer(
    previous?.odometerKm,
    odometerKm,
    previousTimestamp,
    timestamp,
  );
  const impliedDistance = impliedSpeedFromDistance(
    previous?.lat,
    previous?.lon,
    frame.lat,
    frame.lon,
    previousTimestamp,
    timestamp,
  );

  const effectiveSpeed =
    resolveEffectiveSpeed(
      frame.speed,
      previous?.odometerKm,
      odometerKm,
      previous?.lat,
      previous?.lon,
      frame.lat,
      frame.lon,
      previousTimestamp,
      timestamp,
    ) ?? null;

  const speedSource: SpeedSource =
    frame.speed != null
      ? "reported"
      : impliedOdometer != null
        ? "odometer"
        : impliedDistance != null
          ? "distance"
          : "none";

  const hasCoordinates =
    previous?.lat != null &&
    previous?.lon != null &&
    frame.lat != null &&
    frame.lon != null;
  const isGpsGlitch =
    (previous?.lat === 0 && previous?.lon === 0) ||
    (frame.lat === 0 && frame.lon === 0);

  const speedCandidates: SpeedCandidate[] = [
    {
      source: "reported",
      value: frame.speed ?? null,
      reason: frame.speed != null ? null : "notReported",
    },
    {
      source: "odometer",
      value: impliedOdometer,
      reason:
        impliedOdometer != null
          ? null
          : speedReason(
              previous?.odometerKm != null && odometerKm != null,
              false,
              !!previousTimestamp && !!timestamp,
              deltaMs,
            ),
    },
    {
      source: "distance",
      value: impliedDistance,
      reason:
        impliedDistance != null
          ? null
          : speedReason(
              hasCoordinates,
              isGpsGlitch,
              !!previousTimestamp && !!timestamp,
              deltaMs,
            ),
    },
  ];

  const { status, idleSince } = determineVehicleStatus(
    effectiveSpeed,
    ignitionState,
    timestamp,
    previous?.status,
    undefined,
    previous?.idleSince,
    resolved.idle,
  );

  const isMoving =
    effectiveSpeed != null && effectiveSpeed > IDLE_CONFIG.movementThreshold;
  const isStale = isStaleTimestamp(timestamp);

  const rule: StatusRuleId = !timestamp
    ? "noTimestamp"
    : isStale
      ? "stale"
      : isMoving
        ? "moving"
        : resolved.idle === true
          ? "idleSignalOn"
          : resolved.idle === false
            ? "idleSignalOff"
            : ignitionState === true
              ? status === "ralenti"
                ? "ignitionOnIdle"
                : "ignitionOnStopped"
              : ignitionState === false
                ? "ignitionOff"
                : effectiveSpeed != null
                  ? "speedKnown"
                  : "fallback";

  const ignitionHit = findCandidateHit(params, sources.ignition);
  const ignitionInFrame = findCandidateHit(frame.params, sources.ignition) != null;
  const idleInFrame = findCandidateHit(frame.params, sources.idle) != null;
  const ignitionSource: IgnitionSource =
    directIgnition != null
      ? ENGINE_ON_CODES.has(Number(engineStatus))
        ? "engineStatus"
        : frameIgnition === true
          ? "frameIgnition"
          : engineStatus != null
            ? "engineStatus"
            : "frameIgnition"
      : ignitionHit != null
        ? "param"
        : "none";

  const idleHit =
    (resolved.idle === true ? findActiveFlagHit(params, sources.idle) : null) ??
    findCandidateHit(params, sources.idle);
  const idleForMs =
    idleSince != null && timestamp ? timestampToMs(timestamp) - idleSince : null;

  const framePanic = findParamSource(frame.params, "panic");
  const framePanicValue = derivePanic(framePanic?.value, frame.eventCode);
  const frameGpsSat = findParamSource(frame.params, "gps_sat");
  const paramsOdometer = odometerKmFromParams(params);

  const base = { params, frameParams: frame.params };

  const metrics: MetricExplanation[] = [
    explainMetric({
      ...base,
      metric: "battery",
      candidates: sources.battery,
      value: battery,
      paramValue: resolved.battery,
      previousValue: previous?.battery ?? null,
      frameField:
        frame.battery != null ? { key: "battery", raw: frame.battery } : null,
    }),
    explainMetric({
      ...base,
      metric: "satellites",
      candidates: sources.satellites,
      value: gpsSat,
      paramValue: frameGpsSat?.value ?? resolved.satellites,
      previousValue: previous?.gpsSat ?? null,
    }),
    explainMetric({
      ...base,
      metric: "odometer",
      candidates: sources.odometer,
      value: odometerKm,
      paramValue: paramsOdometer,
      previousValue: previous?.odometerKm ?? null,
    }),
    explainMetric({
      ...base,
      metric: "ignition",
      candidates: sources.ignition,
      value: ignitionState,
      paramValue: resolved.ignition,
      frameField:
        ignitionSource === "engineStatus"
          ? { key: "engine_status", raw: engineStatus as number }
          : ignitionSource === "frameIgnition"
            ? { key: "ignition", raw: frameIgnition as boolean }
            : null,
    }),
    explainMetric({
      ...base,
      metric: "sos",
      candidates: sources.sos,
      value: framePanicValue ?? resolved.sos,
      paramValue: resolved.sos,
      frameField:
        framePanicValue != null
          ? { key: framePanic ? "panic" : "event_code", raw: framePanicValue }
          : null,
    }),
    explainMetric({
      ...base,
      metric: "engineCutoff",
      candidates: sources.engineCutoff,
      value: resolved.engineCutoff ?? previous?.engineCutoff ?? null,
      paramValue: resolved.engineCutoff,
      previousValue: previous?.engineCutoff ?? null,
    }),
    explainMetric({
      ...base,
      metric: "hitch",
      candidates: sources.hitch,
      value: resolved.hitch ?? previous?.hitch ?? null,
      paramValue: resolved.hitch,
      previousValue: previous?.hitch ?? null,
    }),
    explainMetric({
      ...base,
      metric: "idle",
      candidates: sources.idle,
      value: resolved.idle,
      paramValue: resolved.idle,
      candidateHit: idleHit,
    }),
  ];

  return {
    status,
    rule,
    idleSince,
    timestampMs: timestamp ? timestampToMs(timestamp) : null,
    isStale,
    previousStatus: previous?.status ?? null,
    reportedSpeed: frame.speed ?? null,
    effectiveSpeed,
    speedSource,
    speedCandidates,
    isMoving,
    movementThreshold: IDLE_CONFIG.movementThreshold,
    deltaMs,
    minDeltaMs: MIN_IMPLIED_SPEED_DELTA_MS,
    ignition: {
      value: ignitionState,
      source: ignitionSource,
      engineStatus,
      frameIgnition,
      paramKey: ignitionHit?.hit?.key ?? null,
      paramRaw: ignitionHit?.hit?.raw ?? null,
      paramScope: ignitionHit?.candidate.scope ?? null,
      inCurrentFrame: ignitionInFrame,
      expected: sources.ignition.flatMap((candidate) => candidate.sources),
    },
    idle: {
      value: resolved.idle,
      supported: sources.idle.length > 0,
      expected: sources.idle.flatMap((candidate) => candidate.sources),
      paramKey: idleHit?.hit?.key ?? null,
      paramRaw: idleHit?.hit?.raw ?? null,
      paramScope: idleHit?.candidate.scope ?? null,
      inCurrentFrame: idleInFrame,
      inferredFromIgnition: resolved.idle == null && ignitionState === true,
      idleForMs,
      thresholdMs: IDLE_CONFIG.idleThreshold,
    },
    metrics,
    params,
    changedKeys,
    brandModel,
    brandName,
    next: {
      params,
      status,
      idleSince,
      odometerKm,
      battery,
      gpsSat,
      engineCutoff: resolved.engineCutoff ?? previous?.engineCutoff ?? null,
      hitch: resolved.hitch ?? previous?.hitch ?? null,
      lat: frame.lat ?? previous?.lat ?? null,
      lon: frame.lon ?? previous?.lon ?? null,
      timestamp: timestamp ?? previousTimestamp,
      brandModel,
      brandName,
    },
  };
};
